import { Typography } from "@mui/material";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";

import { ActionButton } from "./ActionButton";

export function ReplyControls({
  availableCount,
  assignmentCount,
  assignmentNeedsReplyCount,
  canSendInitials,
  texterSendInProgress,
  texterSendBatchSize,
  handleEnqueueTexterSend,
  handleReleaseAssignments,
  handleRequestAssignmentBatch,
  goToCampaign,
}: {
  assignmentCount: number;
  availableCount: number;
  assignmentNeedsReplyCount: number;
  canSendInitials: boolean;
  handleReleaseAssignments: () => void;
  handleRequestAssignmentBatch: () => void;
  goToCampaign: () => void;
  texterSendInProgress: boolean;
  texterSendBatchSize: number | null;
  handleEnqueueTexterSend: () => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const goToRepliesControl = (
    <ActionButton onClick={goToCampaign}>
      {assignmentNeedsReplyCount && !subaccount.sendingDisabled ? (
        <FormattedMessage
          defaultMessage="Reply ({assignmentNeedsReplyCount})"
          description="Button text for navigating to an campaign with replies"
          values={{
            assignmentNeedsReplyCount,
          }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="View Threads"
          description="Button text for navigating to an campaign with replies when sending is disabled"
        />
      )}
    </ActionButton>
  );

  const releaseRepliesControl = (
    <LinkButton
      sx={{ display: "block" }}
      onClick={() => handleReleaseAssignments()}
    >
      <Typography variant="body3">
        <FormattedMessage
          defaultMessage="Release Threads"
          description="Button text for releasing campaign threads"
        />
      </Typography>
    </LinkButton>
  );

  const claimRepliesButton = (
    <ActionButton
      color="primary"
      onClick={() => handleRequestAssignmentBatch()}
    >
      <FormattedMessage
        defaultMessage="Claim Replies"
        description="Button text for claiming campaign replies"
      />
    </ActionButton>
  );

  if (texterSendInProgress) {
    return (
      <Typography variant="h4Caps" sx={{ color: palette.darkGray }}>
        <FormattedMessage
          defaultMessage="Sending {batchSize} Initials..."
          description="Message for when a texter campaign send is in progress"
          values={{
            // fixme: shouldn't be nullable?
            batchSize: texterSendBatchSize?.toLocaleString(),
          }}
        />
      </Typography>
    );
  }

  const sendInitialsButton = (
    <ActionButton color="secondary" onClick={() => handleEnqueueTexterSend()}>
      <FormattedMessage
        defaultMessage="Send initials"
        description="Text for button to send initials"
      />
    </ActionButton>
  );

  return (
    <FlexBox
      sx={{ float: "right" }}
      flexDirection="column"
      gap="6px"
      alignItems="center"
    >
      {assignmentCount
        ? goToRepliesControl
        : availableCount
          ? claimRepliesButton
          : undefined}
      {canSendInitials ? sendInitialsButton : undefined}
      {assignmentNeedsReplyCount ? releaseRepliesControl : undefined}
    </FlexBox>
  );
}
